import { graphql, PageProps } from "gatsby";
import React, { FC, useMemo } from "react";

import { CommonColors } from "../../../types/colors";
import { getTabQueryParam } from "../../../utils/tabs";
import { Renderer } from "../../renderer";
import { Tabs, TabsProps, TabsVariants } from "../../tabs";

export interface TabsSectionProps extends Queries.TabsSectionDataFragment {
  location: PageProps["location"];
}

export const TabsSection: FC<TabsSectionProps> = ({
  tabs,
  slug,
  location,
  variant,
  color,
  title,
}) => {
  const tabsData = useMemo<TabsProps["tabs"]>(() => {
    return (tabs ?? [])
      .map((tab, i) => {
        if (!tab?.title) return null;
        const { title, subTitle, strips } = tab;
        return {
          id: getTabQueryParam(title, i).value,
          body: (
            <>
              {strips?.map((strip, index) => {
                if (!strip) return null;
                return <Renderer key={index} index={index} {...strip} location={location} />;
              })}
            </>
          ),
          header: title,
          subHeader: subTitle ?? undefined,
        };
      })
      .filter(Boolean) as TabsProps["tabs"];
  }, [tabs, location]);

  if (!tabsData.length) return null;

  return (
    <Tabs
      title={title}
      variant={(variant as TabsVariants) ?? undefined}
      colorScheme={(color as CommonColors) ?? undefined}
      tabs={tabsData}
      bodyClassName="mt-9"
      searchParamKey={slug || undefined}
      initialTab={tabsData[0].id}
    />
  );
};

export const query = graphql`
  fragment TabsSectionData on ContentfulTabsSection {
    __typename
    slug
    variant
    color
    title
    tabs {
      title
      subTitle
      strips {
        __typename
        ... on ContentfulHeroSliderSection {
          ...HeroSliderSectionData
        }
        ... on ContentfulMaterialsSlider {
          ...MaterialsSliderData
        }
        ... on ContentfulAnimatedSliderSection {
          ...AnimatedSliderSectionData
        }
        ... on ContentfulVerticalSliderSection {
          ...VerticalSliderSectionData
        }
        ... on ContentfulParallaxBannerSection {
          ...ParallaxBannerSectionData
        }
        ... on ContentfulEditorialText {
          ...EditorialTextData
        }
        ... on ContentfulBannerImage {
          ...BannerImageData
        }
        ... on ContentfulWorldMap {
          ...WorldMapData
        }
        ... on ContentfulHeroBanner {
          ...HeroBannerData
        }
        ... on ContentfulBannerGrid {
          ...BannerGridData
        }
        ... on ContentfulBannerZip {
          ...BannerZipData
        }
        ... on ContentfulBannerCards {
          ...BannerCardsData
        }
        ... on ContentfulAccordionSection {
          ...AccordionSectionData
        }
        ... on ContentfulEmphasisSliderSection {
          ...EmphasisSliderSectionData
        }
        ... on ContentfulGrid {
          ...GridData
        }
        ... on ContentfulSpecialContent {
          ...SpecialContentData
        }
        ... on ContentfulJobsGridSection {
          ...JobsGridSectionData
        }
        ... on ContentfulTableSection {
          ...TableSectionData
        }
        ... on ContentfulFileCategory {
          ...FileCategoryData
        }
        ... on ContentfulHeroPortrait {
          ...HeroPortraitData
        }
        ... on ContentfulCompanyCalendar {
          ...CompanyCalendarData
        }
        ... on ContentfulEmbeddedContent {
          ...EmbeddedContentData
        }
        ... on ContentfulAnchor {
          ...AnchorData
        }
        ... on ContentfulProductFinderSection {
          ...ProductFinderSectionData
        }
        ... on ContentfulEventsGridSection {
          ...EventsGridSectionData
        }
      }
    }
  }
`;
